import { useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, CalendarClock, X } from "lucide-react";
import { fmtDate } from "@/lib/api";

const dismissKey = (kind, day) => `neec_banner_${kind}_${day}`;
const todayKey = () => new Date().toISOString().slice(0, 10);

export function BillingBanner({ user }) {
  const st = user?.account_status;
  const kind = st === "trialing" ? "trial" : st === "past_due" ? "past_due" : st === "active" && user?.cancel_at_period_end ? "cancel" : null;
  const [dismissed, setDismissed] = useState(() => kind ? !!localStorage.getItem(dismissKey(kind, todayKey())) : false);
  if (!kind || dismissed) return null;
  const dismiss = () => { localStorage.setItem(dismissKey(kind, todayKey()), "1"); setDismissed(true); };
  const days = user.trial_days_remaining;
  const endsToday = user.trial_ends_at && new Date(user.trial_ends_at).toDateString() === new Date().toDateString();
  let cls = "bg-blue-50 border-blue-200 text-blue-900", Icon = CalendarClock, text = "", cta = "Choose a plan";
  if (kind === "trial") {
    text = endsToday || days === 0 ? "Your free trial ends today. Choose a plan to keep uninterrupted access." : days === 1 ? "Your free trial ends tomorrow. Choose a plan to continue using NEEC Service." : `Your NEEC Service free trial ends on ${fmtDate(user.trial_ends_at)}. Subscribe to keep uninterrupted access.`;
    if (days <= 1 || endsToday) cls = "bg-amber-50 border-amber-200 text-amber-900";
  } else if (kind === "past_due") {
    cls = "bg-red-50 border-red-200 text-red-900"; Icon = AlertTriangle; cta = "Update billing";
    text = `Your NEEC Service payment could not be processed. Please update your payment method to avoid interruption on ${fmtDate(user.grace_period_ends_at)}.`;
  } else {
    cls = "bg-slate-100 border-slate-200 text-slate-800"; cta = "Manage billing";
    text = `Subscription scheduled to end on ${fmtDate(user.cancellation_effective_at)}. You keep full access until then.`;
  }
  return (
    <div className={`mb-5 rounded-xl border px-4 py-3 text-sm flex flex-wrap items-center gap-x-3 gap-y-2 ${cls}`} data-testid={`billing-banner-${kind}`}>
      <Icon size={16} className="shrink-0" />
      <span className="flex-1 min-w-[200px]" data-testid="billing-banner-text">{text}</span>
      {kind === "trial" && <span className="text-xs font-semibold whitespace-nowrap" data-testid="billing-banner-plans">Monthly $20/mo · Annual $200/yr</span>}
      <Link to="/settings?tab=subscription&focus=plans" role="button" className="inline-flex items-center min-h-[36px] px-3 rounded-lg font-semibold underline underline-offset-2 whitespace-nowrap hover:bg-black/5 focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-500" data-testid="billing-banner-cta">{cta}</Link>
      {kind !== "past_due" && <button type="button" onClick={dismiss} className="p-1 rounded hover:bg-black/5" aria-label="Dismiss for today" data-testid="billing-banner-dismiss"><X size={14} /></button>}
    </div>
  );
}
